import React, { createContext, useContext, useMemo } from 'react'
import type { FeatureKey } from '@hapana/shared/schemas/entitlements/feature-key.schema'
import type { FeatureCheckResult } from '@hapana/shared/schemas/entitlements/entitlement-result.schema'

/** What a lock surface hands over when the user asks to be upgraded. */
export interface UpgradeRequest {
  feature: FeatureKey
  /** The result that produced the lock — carries `upgradeInfo.requiredPlan` / `requiredTheme`. */
  checkResult: FeatureCheckResult
  /** Human label for the thing that was clicked, when the surface knows it (a card title, a nav item). */
  surfaceLabel?: string
}

export type RequestUpgrade = (request: UpgradeRequest) => void

/**
 * How a locked surface asks the host app to start an upgrade conversation.
 *
 * The lock components live in `shared/web` and are used by both Core and Admin, but the support
 * sidebar that actually raises the request is Core-only. Rather than have shared code import a
 * Core module (it cannot) or every call site pass a handler down (there are three unrelated lock
 * renderings), the host mounts a provider and the lock surfaces read it from context.
 *
 * Unprovided — which is Admin today, and any test that renders a gate in isolation — resolves to
 * `null`, and the surfaces keep their previous inert behaviour. Locks must never become dead
 * buttons in an app that has nowhere to send the request.
 */
const UpgradeRequestContext = createContext<RequestUpgrade | null>(null)

export const UpgradeRequestProvider: React.FC<
  React.PropsWithChildren<{ onRequestUpgrade: RequestUpgrade }>
> = ({ onRequestUpgrade, children }) => {
  // The handler closes over sidebar setters that change identity on every render, so memoise on the
  // callback itself; providers pass a `useCallback` handler.
  const value = useMemo(() => onRequestUpgrade, [onRequestUpgrade])
  return <UpgradeRequestContext.Provider value={value}>{children}</UpgradeRequestContext.Provider>
}

/** `null` when no host is listening — callers must treat that as "leave the surface inert". */
export function useUpgradeRequest(): RequestUpgrade | null {
  return useContext(UpgradeRequestContext)
}

/**
 * Props that make a locked surface behave like a button, or `{}` when nothing is listening.
 *
 * Shared so the three lock renderings (route gate, control-centre tile, sidebar nav) cannot drift
 * on accessibility: each becomes a real button with a keyboard path, or stays exactly as it was.
 */
export function useUpgradeAffordance(
  request: UpgradeRequest | null,
  label: string
): Record<string, unknown> {
  const requestUpgrade = useUpgradeRequest()
  return useMemo(() => {
    if (!requestUpgrade || !request || request.checkResult.entitled) {
      return {}
    }
    const fire = (event: React.SyntheticEvent) => {
      // The gate sits inside cards and nav items that navigate; without this the click both opens
      // the sidebar and routes to a page the user is not entitled to see.
      event.preventDefault()
      event.stopPropagation()
      requestUpgrade(request)
    }
    return {
      role: 'button',
      tabIndex: 0,
      'aria-label': label,
      onClick: fire,
      onKeyDown: (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          fire(event)
        }
      },
      // Deliberately no `sx`: callers merge their own styles and a returned `sx` would either be
      // clobbered by their spread or clobber theirs, depending on spread order. Cursor is the
      // caller's to apply — they know which node is the hover target.
    }
  }, [requestUpgrade, request, label])
}
